import { useEffect, useRef } from "react";
import { fetchArticles } from "@/services/news";
import {
  getLastSeenArticleId,
  isNotificationSupported,
  setLastSeenArticleId,
  showNewsNotification,
} from "@/lib/notifications";
import { useUiStore } from "@/stores/ui";

const POLL_MS = 90_000;

/**
 * Com notificações ativas, verifica novas matérias e dispara alerta no navegador
 * enquanto a aba estiver aberta (fallback). O aviso principal com site fechado
 * vem do Web Push (Service Worker + backend).
 */
export function NewsAlertWatcher() {
  const notificationsEnabled = useUiStore((s) => s.notificationsEnabled);
  const primed = useRef(false);

  useEffect(() => {
    if (!notificationsEnabled || !isNotificationSupported()) return;
    if (Notification.permission !== "granted") return;

    let cancelled = false;

    async function checkLatest() {
      try {
        const data = await fetchArticles({ ordering: "-publish_at", page_size: 1 });
        if (cancelled) return;
        const latest = data.results?.[0];
        if (!latest) return;

        const lastSeen = getLastSeenArticleId();
        if (!primed.current || lastSeen == null) {
          setLastSeenArticleId(latest.id);
          primed.current = true;
          return;
        }

        if (latest.id !== lastSeen) {
          showNewsNotification(latest);
          setLastSeenArticleId(latest.id);
        }
      } catch {
        // Rede/API indisponível — tenta de novo no próximo ciclo.
      }
    }

    void checkLatest();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void checkLatest();
    }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") void checkLatest();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [notificationsEnabled]);

  return null;
}
